import {useContext} from 'react';

import {FormikProvider, useFormik} from 'formik';
import {ErrorMessage, Field} from 'formik';
import {useTranslation} from 'react-i18next';

import {Alert, Checkbox, FormControlLabel, Grid, Radio, RadioGroup, Typography} from '@mui/material';

import {EmpionButton} from '../EmpionButton/EmpionButton';
import {EmpionSelectField} from '../EmpionSelectField/EmpionSelectField';
import {EmpionTextField} from '../EmpionTextField/EmpionTextField';

import {LanguageContext} from '../../contexts/LanguageContext/LanguageContext';
import {getTextFieldStyle} from '../../features/user/styles/getTextFieldStyle';
import {useRejectionReasons} from '../../hooks/useRejectionReasons';
import {greenColors, orangeColors} from '../../layouts/theme/designSystem';
import {
  ApplicantRejectionReasonBody,
  CompanyRejectionReasonBody,
} from '../../lib/api/RequestBodies/shared-recruiting/SharedRecruitingRequestBodies';
import {ApiResponseError} from '../../lib/api/v1/types';
import {Rejection} from '../../types/shared-recruiting-types';

interface CommonProps {
  isUpdatingMatch: boolean;
  error?: ApiResponseError;
}

interface CompanyProps extends CommonProps {
  rejectionType: 'company';
  onReject: (body: CompanyRejectionReasonBody) => void;
}

interface ApplicantProps extends CommonProps {
  rejectionType: 'applicant';
  onReject: (body: ApplicantRejectionReasonBody) => void;
}

type Props = CompanyProps | ApplicantProps;

export const RejectionReasonModal = ({rejectionType, onReject, isUpdatingMatch, error}: Props) => {
  const {t} = useTranslation();

  const currentLanguage = useContext(LanguageContext);
  const {data, isLoading} = useRejectionReasons(rejectionType);

  const rejectionOtherKey = rejectionType === 'company' ? 'other_company' : 'other_applicant';
  const isCompany = rejectionType === 'company';

  const handleOptions = (data: Rejection[]) => {
    const sortedData = data
      .map((reason: Rejection) => ({
        id: reason.rejectionReasonKey,
        label: reason.rejectionReasonMessage[currentLanguage],
      }))
      .sort((a, b) => a.label.localeCompare(b.label));

    const otherOptionIndex = sortedData.findIndex((option) => option.id === rejectionOtherKey);

    if (otherOptionIndex !== -1) {
      const otherOption = sortedData.splice(otherOptionIndex, 1)[0];
      sortedData.push(otherOption);
    }

    return sortedData;
  };

  const formik = useFormik({
    initialValues: {
      rejectionReasonKey: '',
      rejectionReasonMessage: '',
      talentNotified: false,
      talentEmailMode: 'standard',
      talentMessage: '',
    },

    onSubmit: (values) => {
      const baseBody: ApplicantRejectionReasonBody = {
        rejectionReason: {
          rejectionReasonKey: values.rejectionReasonKey,
          rejectionReasonMessage: values.rejectionReasonMessage,
        },
      };

      if (rejectionType === 'applicant') {
        onReject(baseBody);
        return;
      }

      const isCustomMode = values.talentEmailMode === 'custom';
      onReject({
        ...baseBody,
        talentNotified: values.talentNotified,
        talentMessage: values.talentNotified && isCustomMode ? values.talentMessage.trim() : '',
      });
    },

    validate: (values) => {
      const errors: any = {};

      if (!values.rejectionReasonKey) {
        errors.rejectionReasonKey = t('shared.required');
      }

      if (values.rejectionReasonKey === rejectionOtherKey && !values.rejectionReasonMessage.trim()) {
        errors.rejectionReasonMessage = t('shared.required');
      }

      const isCustomMode = values.talentEmailMode === 'custom';
      if (values.talentNotified && isCustomMode && !values.talentMessage.trim()) {
        errors.talentMessage = t('shared.rejection.talentMessageRequired');
      }

      return errors;
    },
  });

  return (
    <Grid
      container
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '300px',
      }}
    >
      <Grid item textAlign={'center'}>
        <Typography variant='h3'>{t('shared.rejection.title')}</Typography>
      </Grid>
      <Grid item xs={12} mt={{xs: 2, sm: 3, md: 5}} width={'100%'}>
        <FormikProvider value={formik}>
          <form onSubmit={formik.handleSubmit}>
            <Typography variant='subtitle1' mb={1}>
              {t('shared.rejection.empionFeedbackSectionTitle')}
            </Typography>
            <Field
              type='select'
              name={'rejectionReasonKey'}
              component={EmpionSelectField}
              options={handleOptions(Array.isArray(data) ? data : [])}
              size={'small'}
              label={t('shared.rejection.rejectionReason')}
              required
              sx={{
                ...getTextFieldStyle(),
                backgroundColor: orangeColors[7],
                color: greenColors[700],
                borderColor: orangeColors[5],
                whiteSpace: {xs: 'normal', sm: 'nowrap'},
              }}
            />
            <Typography variant='body2' color='error' marginTop={1}>
              <ErrorMessage name={'rejectionReasonKey'} component='span' />
            </Typography>

            <Field
              type='text'
              name={'rejectionReasonMessage'}
              component={EmpionTextField}
              size={'small'}
              multiline
              rows={4}
              fullWidth
              required
              label={t('shared.rejection.otherReason')}
              sx={{
                ...getTextFieldStyle(),
                backgroundColor: orangeColors[7],
                color: greenColors[700],
                borderColor: orangeColors[5],
                width: '100%',
                mt: {xs: 1, sm: 2, md: 3},
              }}
            />

            <Typography variant='body2' color='error' marginTop={1}>
              <ErrorMessage name={'rejectionReasonMessage'} component='span' />
            </Typography>
            {isCompany && (
              <>
                <Typography variant='subtitle1' mt={{xs: 2, sm: 3, md: 4}} mb={1}>
                  {t('shared.rejection.talentMessageSectionTitle')}
                </Typography>

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formik.values.talentNotified}
                      onChange={(event) => formik.setFieldValue('talentNotified', event.target.checked)}
                    />
                  }
                  label={t('shared.rejection.notifyTalentLabel')}
                />

                {formik.values.talentNotified && (
                  <>
                    <Typography variant='body2' sx={{mb: 0.5}}>
                      {t('shared.rejection.talentMessageModeLabel')}
                    </Typography>
                    <RadioGroup
                      value={formik.values.talentEmailMode}
                      onChange={(event) => formik.setFieldValue('talentEmailMode', event.target.value)}
                      sx={{mb: 1.5}}
                    >
                      <FormControlLabel
                        value='standard'
                        control={<Radio />}
                        label={t('shared.rejection.standardEmailOption')}
                      />
                      <FormControlLabel
                        value='custom'
                        control={<Radio />}
                        label={t('shared.rejection.customTextOption')}
                      />
                    </RadioGroup>
                  </>
                )}

                {formik.values.talentNotified && (
                  <>
                    <Alert severity='info' sx={{mb: 2}}>
                      {t('shared.rejection.talentMessageInfo')}
                    </Alert>

                    <Field
                      type='text'
                      name={'talentMessage'}
                      component={EmpionTextField}
                      size={'small'}
                      multiline
                      rows={4}
                      fullWidth
                      required={formik.values.talentEmailMode === 'custom'}
                      disabled={formik.values.talentEmailMode !== 'custom'}
                      label={t('shared.rejection.talentMessageLabel')}
                      placeholder={t('shared.rejection.talentMessagePlaceholder')}
                      sx={{
                        ...getTextFieldStyle(),
                        backgroundColor: orangeColors[7],
                        color: greenColors[700],
                        borderColor: orangeColors[5],
                        width: '100%',
                      }}
                    />

                    <Typography variant='body2' color='error' marginTop={1} mb={{xs: 2, sm: 3, md: 4}}>
                      <ErrorMessage name={'talentMessage'} component='span' />
                    </Typography>
                  </>
                )}
              </>
            )}
            <Grid
              item
              xs={12}
              sx={{
                display: {xs: 'block', sm: 'flex'},
                justifyContent: 'flex-end',
              }}
            >
              <EmpionButton type='submit' disabled={isLoading || isUpdatingMatch}>
                {t('shared.formButtons.save')}
              </EmpionButton>
            </Grid>
          </form>
        </FormikProvider>
        {error && (
          <Typography variant='body1' color={'error'}>
            {t('shared.tags.error')}
          </Typography>
        )}
      </Grid>
    </Grid>
  );
};
